import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { environment } from 'src/environments/environment';

interface HelpTextPart {
  text: string;
  url?: string;
}

@Component({
  selector: 'app-open-help-dialog-modal',
  templateUrl: './open-help-dialog-modal.component.html',
  styleUrls: ['./open-help-dialog-modal.component.scss']
})
export class OpenHelpDialogModalComponent implements OnInit {
  sectionName!: string;

  helpText!: string;
  helpTextParts: HelpTextPart[] = [];
  isEditing: boolean = false;
  editedText: string = '';

  constructor(
    public modalRef: BsModalRef,
    private httpService: HttpClient
  ) {}

  ngOnInit(): void {
    this.getHelpText();
  }

  trackByPart(index: number): number {
    return index;
  }

  // Splits the help text into plain-text and URL parts so the template can
  // render real Angular-managed <a> elements instead of injecting raw HTML.
  // This is computed once whenever the help text changes (not as a getter),
  // so the rendered DOM stays stable. A getter would return a new array on
  // every change-detection cycle, causing *ngFor to rebuild the nodes on each
  // mouse event - which broke text selection and link clicks inside the modal.
  private buildHelpTextParts(text: string): HelpTextPart[] {
    const parts: HelpTextPart[] = [];
    // Matches full URLs (http/https) as well as "www." prefixed addresses
    // that have no protocol, so links typed without "https://" still work.
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = urlRegex.exec(text)) !== null) {
      // Preceding plain text.
      if (match.index > lastIndex) {
        parts.push({ text: text.slice(lastIndex, match.index) });
      }

      // Keep trailing punctuation outside of the link.
      let display = match[0];
      let trailing = '';
      const trailingMatch = /[.,;:!?)\]]+$/.exec(display);
      if (trailingMatch) {
        trailing = trailingMatch[0];
        display = display.slice(0, -trailing.length);
      }

      // Ensure the href has a protocol so the browser navigates correctly.
      const href = /^https?:\/\//i.test(display) ? display : `https://${display}`;

      parts.push({ text: display, url: href });
      if (trailing) {
        parts.push({ text: trailing });
      }

      lastIndex = match.index + match[0].length;
    }

    // Remaining plain text.
    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex) });
    }

    return parts;
  }

  async getHelpText() {
    const endpoint = `${environment.baseUrl}/help-texts/get-help-text/${this.sectionName}`;

    const options = {
      observe: 'body' as const,
      responseType: 'json' as const
    };

    const result = await this.httpService.get<{ text: string }>(endpoint, options).toPromise();

    this.helpText = result.text;
    this.helpTextParts = this.buildHelpTextParts(this.helpText ?? '');
  }

  async saveHelpText() {
    const endpoint = `${environment.baseUrl}/help-texts/upsert-help-text`;
    const body = {
      section: this.sectionName,
      text: this.editedText
    };

    const options = {
      observe: 'body' as const,
      responseType: 'json' as const
    };

    const result = await this.httpService.post(endpoint, body, options).toPromise();

    console.log('Help text saved:', result);
  }

  // Bearbeitungsmodus aktivieren
  enableEdit(): void {
    this.isEditing = true;
    this.editedText = this.helpText; // Aktuellen Text in die Zwischenvariable laden
  }

  // Bearbeitungsmodus speichern
  async saveEdit() {
    await this.saveHelpText();
    this.isEditing = false;
    await this.getHelpText();
  }

  // Bearbeitung abbrechen
  cancelEdit(): void {
    this.isEditing = false; // Änderungen verwerfen
    this.editedText = ''; // Zwischenvariable zurücksetzen
  }
}
